import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hash, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { safeInvoke } from '../lib/safeInvoke';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { ReadingBasis } from '../components/shared/ReadingBasis';
import { EmptyState } from '../components/ui/EmptyState';
import { ToolPageShell } from '../components/tool';
import {
  computeAllNumbers,
  displayNumberForKey,
  type AllNumbers,
} from '../lib/numerology';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DailyResult {
  personalNumber: number;
  text: string;
}

interface ForecastItem {
  date: string;
  number: number;
  label: string;
  text?: string;
  shortDate?: string;
}

interface ForecastResult {
  type: string;
  personalNumber: number;
  text: string;
  items: ForecastItem[];
  summary: string;
}

interface MapaResult {
  sections: Record<string, string>;
  readingId: string | null;
  text: string;
}

type Mode         = 'mapa' | 'previsoes';
type ForecastType = 'weekly' | 'monthly' | 'yearly';

// ─── Mapa section groups ──────────────────────────────────────────────────────

const MAPA_GROUPS: Array<{
  id:    string;
  title: string;
  keys:  string[];
}> = [
  {
    id:    'fundamentais',
    title: 'Números Fundamentais',
    keys:  ['caminhoDeVida', 'destino', 'alma', 'personalidade', 'expressao', 'impressao', 'motivacao', 'maturidade', 'missao', 'proposito'],
  },
  {
    id:    'ciclosPessoais',
    title: 'Ciclos Pessoais',
    keys:  ['anoPessoal', 'mesPessoal', 'diaPessoal'],
  },
  {
    id:    'ciclosDesafios',
    title: 'Ciclos e Desafios',
    keys:  ['ciclosDeVida', 'desafios', 'pontosMaturidade'],
  },
  {
    id:    'karmica',
    title: 'Lições Kármicas',
    keys:  ['licoesKarmicas'],
  },
  {
    id:    'sintese',
    title: 'Síntese Geral',
    keys:  ['sinteseGeral'],
  },
];

const KEY_LABELS: Record<string, string> = {
  caminhoDeVida:    'Caminho de Vida',
  destino:          'Destino',
  alma:             'Alma',
  personalidade:    'Personalidade',
  expressao:        'Expressão',
  impressao:        'Impressão',
  motivacao:        'Motivação',
  maturidade:       'Maturidade',
  missao:           'Missão',
  proposito:        'Propósito',
  anoPessoal:       'Ano Pessoal',
  mesPessoal:       'Mês Pessoal',
  diaPessoal:       'Dia Pessoal',
  ciclosDeVida:     'Ciclos de Vida',
  desafios:         'Desafios',
  pontosMaturidade: 'Pontos de Maturidade',
  licoesKarmicas:   'Lições Kármicas',
  sinteseGeral:     'Síntese Geral',
};

const ALL_MAPA_TYPES = MAPA_GROUPS.flatMap(g => g.keys);

// ─── Small helpers ────────────────────────────────────────────────────────────

function NumberBadge({ n, label }: { n: string; label: string }) {
  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      gap:            '0.2rem',
      background:     'rgba(31,78,95,0.12)',
      border:         '1px solid rgba(31,78,95,0.25)',
      borderRadius:   'var(--radius-md)',
      padding:        '0.5rem 0.75rem',
      minWidth:       '3.5rem',
      flexShrink:     0,
    }}>
      <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--petrol)', lineHeight: 1 }}>
        {n}
      </span>
      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.2 }}>
        {label}
      </span>
    </div>
  );
}

function ForecastTypeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding:      '0.4rem 1rem',
        borderRadius: 'var(--radius-sm)',
        fontSize:     'var(--text-caption)',
        fontWeight:   active ? 700 : 400,
        background:   active ? 'var(--petrol)' : 'transparent',
        color:        active ? '#fff' : 'var(--text-muted)',
        border:       active ? 'none' : '1px solid var(--border-subtle)',
        cursor:       'pointer',
        transition:   'all 0.15s ease',
      }}
    >
      {children}
    </button>
  );
}

// ─── Daily card ───────────────────────────────────────────────────────────────

function DailyForecastCard({ loading, result, firstName }: {
  loading: boolean;
  result:  DailyResult | null;
  firstName: string;
}) {
  if (loading) {
    return (
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <PhaseLoader
          phases={['Calculando seu número do dia...', 'Consultando a energia numerológica...', 'Preparando sua previsão diária...']}
          interval={3000}
        />
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{
          width:          '2.75rem',
          height:         '2.75rem',
          borderRadius:   'var(--radius-md)',
          background:     'linear-gradient(135deg, var(--petrol) 0%, #0d3a47 100%)',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          flexShrink:     0,
          fontSize:       '1.4rem',
          fontWeight:     700,
          color:          '#fff',
        }}>
          {result.personalNumber}
        </div>
        <div>
          <p style={{ fontWeight: 600, fontSize: 'var(--text-label)', margin: 0 }}>Previsão do Dia</p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', margin: '0.1rem 0 0' }}>
            Número Pessoal do Dia: {result.personalNumber}
          </p>
        </div>
      </div>

      <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
        {result.text.split('\n\n').map((para, i) => (
          <p key={i} style={{ margin: i === 0 ? 0 : '0.75rem 0 0' }}>{para}</p>
        ))}
      </div>

      <ReadingBasis userName={firstName} />
    </div>
  );
}

// ─── Mapa section (collapsible) ───────────────────────────────────────────────

function MapaSection({ group, nums, sections, loading }: {
  group:    typeof MAPA_GROUPS[number];
  nums:     AllNumbers | null;
  sections: Record<string, string>;
  loading:  boolean;
}) {
  const [open, setOpen] = useState(group.id === 'fundamentais');
  const hasAI = group.keys.some(k => sections[k]);

  return (
    <div
      className="card"
      style={{ marginBottom: '0.75rem', padding: 0, overflow: 'hidden' }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width:          '100%',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          padding:        '0.875rem 1rem',
          background:     'transparent',
          border:         'none',
          cursor:         'pointer',
          color:          'var(--text-primary)',
          fontWeight:     600,
          fontSize:       'var(--text-label)',
          textAlign:      'left',
        }}
      >
        <span>{group.title}</span>
        <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          {loading && !hasAI && (
            <span style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 400 }}>
              carregando...
            </span>
          )}
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {group.keys.map(key => {
            const displayNum = nums ? displayNumberForKey(key, nums) : '—';
            const aiText     = sections[key];

            return (
              <div key={key}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.35rem' }}>
                  {displayNum && (
                    <span style={{ fontWeight: 600, fontSize: 'var(--text-label)', color: 'var(--petrol)' }}>
                      {displayNum}
                    </span>
                  )}
                  <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 500 }}>
                    {KEY_LABELS[key] ?? key}
                  </span>
                </div>

                {aiText ? (
                  <p style={{
                    fontSize:   'var(--text-label)',
                    color:      'var(--text-secondary)',
                    lineHeight: 1.65,
                    margin:     0,
                  }}>
                    {aiText}
                  </p>
                ) : loading ? (
                  <div style={{
                    height:       '3.5rem',
                    background:   'rgba(255,255,255,0.04)',
                    borderRadius: 'var(--radius-sm)',
                  }} />
                ) : (
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0, fontStyle: 'italic' }}>
                    Texto não disponível
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Forecast cards ───────────────────────────────────────────────────────────

function ForecastCard({ item }: { item: ForecastItem }) {
  return (
    <div className="card" style={{ padding: '0.875rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: item.text ? '0.625rem' : 0 }}>
        <div style={{
          width:          '2.25rem',
          height:         '2.25rem',
          borderRadius:   'var(--radius-sm)',
          background:     'rgba(31,78,95,0.15)',
          border:         '1px solid rgba(31,78,95,0.3)',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          fontWeight:     700,
          fontSize:       '1.1rem',
          color:          'var(--petrol)',
          flexShrink:     0,
        }}>
          {item.number}
        </div>
        <div>
          <p style={{ fontWeight: 600, fontSize: 'var(--text-label)', margin: 0 }}>{item.label}</p>
          {item.shortDate && (
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0.1rem 0 0' }}>
              {item.shortDate}
            </p>
          )}
        </div>
      </div>

      {item.text && (
        <p style={{
          fontSize:   'var(--text-label)',
          color:      'var(--text-secondary)',
          lineHeight: 1.65,
          margin:     0,
        }}>
          {item.text}
        </p>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function NumerologyPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [mode,            setMode]            = useState<Mode>('mapa');
  const [forecastType,    setForecastType]    = useState<ForecastType>('weekly');
  const [dailyLoading,    setDailyLoading]    = useState(true);
  const [dailyResult,     setDailyResult]     = useState<DailyResult | null>(null);
  const [mapaLoading,     setMapaLoading]     = useState(false);
  const [mapaResult,      setMapaResult]      = useState<MapaResult | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastResult,  setForecastResult]  = useState<ForecastResult | null>(null);
  const [forecastCache,   setForecastCache]   = useState<Partial<Record<ForecastType, ForecastResult>>>({});

  const nums = (profile?.full_name && profile?.birth_date)
    ? computeAllNumbers(profile.full_name, profile.birth_date)
    : null;

  const firstName = profile?.full_name?.split(' ')[0] ?? '';

  // Auto-load daily on mount
  useEffect(() => {
    if (!profile?.birth_date || !profile?.full_name) return;

    setDailyLoading(true);
    safeInvoke<{ personalNumber?: number; text?: string } | null>('numerology-forecast', {
      body:     { type: 'daily' },
      fallback: null,
      timeout:  35_000,
    }).then(({ data }) => {
      if (data?.personalNumber && data?.text) {
        setDailyResult({ personalNumber: data.personalNumber, text: data.text });
      }
      setDailyLoading(false);
    });
  }, [profile?.birth_date, profile?.full_name]);

  // Load mapa when switching to mapa mode
  useEffect(() => {
    if (mode !== 'mapa' || mapaResult) return;
    if (!profile?.birth_date || !profile?.full_name) return;

    setMapaLoading(true);
    safeInvoke<{ sections?: Record<string, string>; readingId?: string | null; text?: string } | null>('numerology', {
      body:     { analysisTypes: ALL_MAPA_TYPES },
      fallback: null,
      timeout:  60_000,
    }).then(({ data }) => {
      if (data) {
        setMapaResult({
          sections:  data.sections  ?? {},
          readingId: data.readingId ?? null,
          text:      data.text      ?? '',
        });
      }
      setMapaLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Load forecast when type changes
  useEffect(() => {
    if (mode !== 'previsoes') return;
    if (forecastCache[forecastType]) {
      setForecastResult(forecastCache[forecastType]!);
      return;
    }
    if (!profile?.birth_date || !profile?.full_name) return;

    setForecastLoading(true);
    setForecastResult(null);
    safeInvoke<ForecastResult | null>('numerology-forecast', {
      body:     { type: forecastType },
      fallback: null,
      timeout:  60_000,
    }).then(({ data }) => {
      if (data) {
        setForecastResult(data);
        setForecastCache(c => ({ ...c, [forecastType]: data }));
      }
      setForecastLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, forecastType]);

  // ── Guard ────────────────────────────────────────────────────────
  if (!profile?.birth_date || !profile?.full_name) {
    return (
      <ToolPageShell icon={Hash} title="Numerologia">
        <EmptyState
          emoji="🔢"
          title="Complete seu perfil"
          subtitle="Precisamos do seu nome completo e data de nascimento para calcular seus números."
          cta="Ir para o perfil"
          ctaAction={() => navigate('/profile')}
        />
      </ToolPageShell>
    );
  }

  // ── Key numbers for header ────────────────────────────────────────
  const headerNums = nums ? [
    { n: String(nums.caminhoDeVida), label: 'Caminho de Vida' },
    { n: String(nums.destino),       label: 'Destino' },
    { n: String(nums.alma),          label: 'Alma' },
    { n: String(nums.anoPessoal),    label: 'Ano Pessoal' },
  ] : [];

  const mapaReadingText = mapaResult?.text ?? '';
  const sections        = mapaResult?.sections ?? {};

  return (
    <ToolPageShell icon={Hash} title="Numerologia" subtitle="Seus números pessoais e previsões numerológicas">

      {/* ── Key numbers row ─────────────────────────────────────────── */}
      {nums && (
        <div style={{
          display:      'flex',
          gap:          '0.5rem',
          overflowX:    'auto',
          padding:      '0.25rem 0 0.75rem',
          marginBottom: '0.75rem',
        }}>
          {headerNums.map(({ n, label }) => (
            <NumberBadge key={label} n={n} label={label} />
          ))}
        </div>
      )}

      {/* ── Daily forecast ───────────────────────────────────────────── */}
      <DailyForecastCard loading={dailyLoading} result={dailyResult} firstName={firstName} />

      {/* ── Mode selector ────────────────────────────────────────────── */}
      <div style={{
        display:      'flex',
        gap:          '0.5rem',
        marginBottom: '1.25rem',
        background:   'rgba(255,255,255,0.04)',
        borderRadius: 'var(--radius-md)',
        padding:      '0.35rem',
        border:       '1px solid var(--border-subtle)',
      }}>
        {([
          { id: 'mapa',      label: 'Mapa Numerológico' },
          { id: 'previsoes', label: 'Previsões' },
        ] as const).map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setMode(id)}
            style={{
              flex:         1,
              padding:      '0.45rem 0',
              borderRadius: 'var(--radius-sm)',
              fontSize:     'var(--text-caption)',
              fontWeight:   mode === id ? 700 : 400,
              background:   mode === id ? 'var(--petrol)' : 'transparent',
              color:        mode === id ? '#fff' : 'var(--text-muted)',
              border:       'none',
              cursor:       'pointer',
              transition:   'all 0.15s ease',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Mapa mode ────────────────────────────────────────────────── */}
      {mode === 'mapa' && (
        <>
          {MAPA_GROUPS.map(group => (
            <MapaSection
              key={group.id}
              group={group}
              nums={nums}
              sections={sections}
              loading={mapaLoading}
            />
          ))}

          {mapaResult && !mapaLoading && (
            <div style={{ marginTop: '0.75rem' }}>
              <ReadingActions
                pageName="Mapa Numerológico"
                reading={mapaReadingText}
                userName={firstName}
              />
              <ReadingFeedback feature="numerology" readingId={mapaResult.readingId ?? undefined} />
            </div>
          )}
        </>
      )}

      {/* ── Previsões mode ───────────────────────────────────────────── */}
      {mode === 'previsoes' && (
        <>
          {/* Period selector */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
            {([
              { id: 'weekly',  label: 'Semana' },
              { id: 'monthly', label: 'Mês' },
              { id: 'yearly',  label: 'Ano' },
            ] as const).map(({ id, label }) => (
              <ForecastTypeButton
                key={id}
                active={forecastType === id}
                onClick={() => setForecastType(id)}
              >
                {label}
              </ForecastTypeButton>
            ))}
          </div>

          {forecastLoading && (
            <PhaseLoader
              phases={[
                'Calculando seus ciclos numerológicos...',
                'Analisando a energia de cada período...',
                'Preparando sua previsão personalizada...',
              ]}
              interval={3500}
            />
          )}

          {forecastResult && !forecastLoading && (
            <>
              {/* Individual period cards */}
              {forecastResult.items.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  {forecastResult.items.map(item => (
                    <ForecastCard key={item.date} item={item} />
                  ))}
                </div>
              )}

              {/* Summary card */}
              {forecastResult.summary && (
                <div className="card" style={{ marginBottom: '1rem' }}>
                  <p style={{
                    fontWeight:   600,
                    fontSize:     'var(--text-label)',
                    color:        'var(--petrol)',
                    marginBottom: '0.5rem',
                  }}>
                    {forecastType === 'weekly'  ? 'Síntese da Semana' :
                     forecastType === 'monthly' ? 'Síntese do Mês'    : 'Síntese do Ano'}
                  </p>
                  {forecastResult.summary.split('\n\n').map((para, i) => (
                    <p key={i} style={{
                      fontSize:   'var(--text-label)',
                      color:      'var(--text-secondary)',
                      lineHeight: 1.7,
                      margin:     i === 0 ? 0 : '0.75rem 0 0',
                    }}>
                      {para}
                    </p>
                  ))}
                  <ReadingBasis userName={firstName} />
                </div>
              )}

              <ReadingActions
                pageName={`Previsão Numerológica — ${
                  forecastType === 'weekly'  ? 'Semana' :
                  forecastType === 'monthly' ? 'Mês'    : 'Ano'
                }`}
                reading={forecastResult.summary || forecastResult.text}
                userName={firstName}
              />
              <ReadingFeedback feature="numerology" />
            </>
          )}
        </>
      )}
    </ToolPageShell>
  );
}
